<script setup>
import {ref, onMounted, computed} from 'vue'
import { useDisplay } from "vuetify"
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import {handleError} from "@/utils/errors.js";
import messageService from '@/services/messages.js'
import {useI18n} from "vue-i18n";
import router from "@/router/index.js";
import {useRoute} from "vue-router";
import {saveConcentrator, getConcentratorById} from "@/services/api/concentrator.js";
import EntityForm from "@/components/common/Form.vue";
import ButtonPreviousNext from "@/components/common/button/ButtonPreviousNext.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import {ConcentratorEntity} from "@/entities/ConcentratorEntity.js";
import {ConcentratorStep} from "@/enums/enums.js"
import {buildPayloadFromEntity, populateFormFromEntity, searchAndSetOptions} from "@/utils/index.js";
import {getManufacturer} from "@/services/api/manufacturer.js";
import {getPOPs} from "@/services/api/POP.js";
import {getAttributeLayout} from "@/services/api/attributeLayout.js";
import {getPools} from "@/services/api/pool.js";

const { t } = useI18n();
const loading = ref(false);
const isMounted = ref(false);
const name = ref('');
const description = ref('');
const route = useRoute();
const disabledInput = ref(false);
const havePermission = ref(false);
const form = ref(null)
const step = ref(1);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const Concentrator = new ConcentratorEntity();
const entity = ref(Concentrator);
const formDataStep1 = ref(entity.value.fieldsStep1);
const formDataStep2 = ref(entity.value.fieldsStep2);
const id = route.params.id;
const isFetching = ref(false);
const { smAndDown } = useDisplay()

async function handleSearch({ handler, query, key }) {
    const handlers = {
        handleSearchManufacturer: () =>
            searchAndSetOptions({
                key: "manufacturer_id",
                apiFn: getManufacturer,
                query,
                entity,
                isFetching,
                formName: "fieldsStep1"
            }),

        handleSearchPOP: () =>
            searchAndSetOptions({
                key,
                apiFn: getPOPs,
                query,
                entity,
                isFetching,
                formName: "fieldsStep1"
            }),


        handleSearchPool: () =>
            searchAndSetOptions({
                key,
                apiFn: getPools,
                query,
                entity,
                isFetching,
                formName: "fieldsStep2"
            }),

        handleSearchLayout: () =>
            searchAndSetOptions({
                key: "layout_id",
                apiFn: getAttributeLayout,
                query,
                entity,
                isFetching,
                formName: "fieldsStep1"
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}

const handleSubmit = async () => {
    const { valid } = await form.value.validate();
    if (!valid) return message.error(t('common.messages.errors.400'));

    loading.value = true;

    try {
        formDataStep1.value.id = id ?? null;
        const payloadStep1 = buildPayloadFromEntity({entity: entity.value, data: formDataStep1.value, formName: "fieldsStep1"});
        const payloadStep2 = buildPayloadFromEntity({entity: entity.value, data: formDataStep2.value, formName: "fieldsStep2"});

        payloadStep1.id = formDataStep1.value.id ?? null;
        await saveConcentrator({...payloadStep1, ...payloadStep2});
        router.push('/isp/concentrators');
    } catch (error) {
        console.error(error);
        message.error(await handleError(error.response, t, name.value));

    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    try {
        if (!id) return;

        isMounted.value = false;
        const { data } = await getConcentratorById({ id });

        const mapFields = [
            "pop",
            "layout",
            "manufacturer",
            "pool_v4_blocked",
            "pool_v4_normal",
            "pool_v6_blocked",
            "pool_v6_normal"
        ];

        mapFields.forEach(field => {
            if (data[field] && typeof data[field] === "object") {
                data[`${field}_id`] = {
                    title: data[field].name,
                    value: data[field].id
                };
            }
        });

        formDataStep1.value = data;
        formDataStep2.value = data;

    } catch (e) {
        console.error(e);
    } finally {
        isMounted.value = true;
    }
});


</script>

<template>
    <BasePage :loading-mounted="isMounted" :entity="entity">
        <template #default>
            <v-card class="bg-white trezo-card px-sm-0">
                <v-stepper v-model="step" :direction="smAndDown ? 'vertical' : 'horizontal'">
                    <v-stepper-header :class="{ 'flex-wrap': smAndDown }" class="overflow-x-auto">
                        <v-stepper-item :title="t('common.labels.generalDetails')"  @click.stop="step = ConcentratorStep.Details" :value="1" />
                        <v-divider></v-divider>
                        <v-stepper-item :title="t('common.labels.technicalSettings')" @click.stop="step = ConcentratorStep.Configuration" :value="2" />
                    </v-stepper-header>

                    <v-stepper-window class="px-sm-0">
                        <v-stepper-window-item :value="1">
                            <EntityForm :entity="entity" fieldsToUse="fieldsStep1" ref="form"  v-model="formDataStep1" @search="handleSearch" />

                            <div class="d-flex justify-end mt-4">
                                <ButtonPreviousNext  :next="true" @click="step = 2" />
                            </div>
                        </v-stepper-window-item>
                        <v-stepper-window-item :value="2">
                            <EntityForm :entity="entity" fieldsToUse="fieldsStep2" ref="form" v-model="formDataStep2" @search="handleSearch" />
                            <div class="d-flex justify-start mt-10">
                                <ButtonPreviousNext size="sm" :next="false" @click="step = ConcentratorStep.Details" />
                            </div>
                            <div class="col-12 d-flex justify-end">
                                <ButtonSave v-if="!disabledInput" :loading="loading" @click="handleSubmit" />
                            </div>
                        </v-stepper-window-item>
                    </v-stepper-window>
                </v-stepper>
            </v-card>
        </template>
    </BasePage>

</template>
